// Footer.js – Fully fixed alignment, responsive, with smooth wave curve
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaEnvelope,
  FaMapMarkerAlt,
  FaClock,
  FaLinkedinIn,
  FaInstagram,
  FaFacebookF,
} from "react-icons/fa";
import "./Footer.css";
import logo from "./icons/alvanium.png";

function Footer() {
  const navigate = useNavigate();
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());

  useEffect(() => {
    setCurrentYear(new Date().getFullYear());
  }, []);

  const quickLinks = [
    { id: "home", label: "Home", path: "/" },
    { id: "aboutalvanium", label: "About Alvanium", path: "/aboutalvanium" },
    { id: "products", label: "Products", path: "/products" },
    { id: "android", label: "Android Apps", path: "/android" },
    { id: "customsolutions", label: "Custom Solutions", path: "/customsolutions" },
    { id: "contact", label: "Contact", path: "/contact" },
  ];

  const services = [
    { id: "jira", label: "Jira", path: "/products/jira" },
    { id: "confluence", label: "Confluence", path: "/products/Confluence" },
    { id: "jsm", label: "Jira Service Management", path: "/products/Jira-Service-Management" },
    { id: "zendesk", label: "Zendesk", path: "/products/Zendesk" },
    { id: "monday", label: "Monday.com", path: "/products/monday.com" },
    { id: "customized-solutions", label: "Custom Solutions", path: "/products/Customized-Solutions" },
  ];

  const socialLinks = [
    { icon: FaLinkedinIn, label: "LinkedIn", url: "https://www.linkedin.com/company/codeformesystems/" },
    { icon: FaInstagram, label: "Instagram", url: "https://www.instagram.com/alvaniumtech?igsh=NHUwaHNodTgwa2Zw" },
    { icon: FaFacebookF, label: "Facebook", url: "https://facebook.com/alvaniumtech" },
  ];

  const legalLinks = [
    { id: "privacy", label: "Privacy Policy", path: "/privacy-policy" },
    { id: "terms", label: "Terms of Service", path: "/terms-of-service" },
  ];

  const handleNavigation = (e, path) => {
    e.preventDefault();
    navigate(path);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleExternalLink = (e, url) => {
    e.preventDefault();
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <footer className="global-footer">
      {/* Smooth wave edge */}
      <div className="global-footer-edge">
        <svg viewBox="0 0 1200 40" preserveAspectRatio="none">
          <path d="M0,40 L0,20 C300,0 600,40 900,10 C1100,-10 1200,20 1200,20 L1200,40 Z" />
        </svg>
      </div>

      <div className="global-footer-inner">
        {/* Brand Section */}
        <div className="gf-brand">
          <div className="logo">
            <img src={logo} alt="Alvanium Logo" className="logo-image" />
            <span className="logo-text">Alvanium</span>
          </div>
          <p>
            Intelligence behind every workflow — smart software,
            Android apps, and platform customization for growing teams.
          </p>
        </div>

        {/* Columns */}
        <div className="gf-columns">
          {/* Quick Links */}
          <div className="gf-col">
            <h4>Quick Links</h4>
            <nav>
              {quickLinks.map((item) => (
                <a
                  key={item.id}
                  href={item.path}
                  onClick={(e) => handleNavigation(e, item.path)}
                >
                  {item.label}
                </a>
              ))}
            </nav>
          </div>

          {/* Services */}
          <div className="gf-col">
            <h4>Services</h4>
            <nav>
              {services.map((service) => (
                <a
                  key={service.id}
                  href={service.path}
                  onClick={(e) => handleNavigation(e, service.path)}
                >
                  {service.label}
                </a>
              ))}
            </nav>
          </div>

          {/* Contact */}
          <div className="gf-col gf-contact-col">
            <h4>Get in Touch</h4>
            <div className="gf-contact-item">
              <span className="gf-icon-badge">
                <FaEnvelope />
              </span>
              <span>info@alvanium.com</span>
            </div>
            <div className="gf-contact-item">
              <span className="gf-icon-badge">
                <FaMapMarkerAlt />
              </span>
              <span>
                Alvanium Office, Elayankanni X Road,
                Moongilthuraipattu, Kallakurichi 605 702
              </span>
            </div>
            <div className="gf-contact-item">
              <span className="gf-icon-badge">
                <FaClock />
              </span>
              <span>Mon–Fri: 9:30 AM – 6:30 PM</span>
            </div>
          </div>

          {/* Social */}
          <div className="gf-col gf-social-col">
            <h4>Follow Us</h4>
            <div className="social-grid">
              {socialLinks.map((social, index) => (
                <a
                  key={index}
                  href={social.url}
                  onClick={(e) => handleExternalLink(e, social.url)}
                  className={`social-link ${social.label.toLowerCase()}`}  // ← FIXED
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <social.icon />
                  <span>{social.label}</span>
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="global-footer-bottom">
        <span>© {currentYear} Alvanium Technologies Inc. All rights reserved.</span>
        <div className="gf-legal">
          {legalLinks.map((link) => (
            <a
              key={link.id}
              href={link.path}
              onClick={(e) => handleNavigation(e, link.path)}
            >
              {link.label}
            </a>
          ))}
        </div>
      </div>
    </footer>
  );
}

export default Footer;